import FormField from '@/shared/Forms/FormField'
import TextInput from '@/shared/Inputs/TextInput'
import SeparateLine from '@/shared/Forms/SeparateLine'
import EquipmentDropdown from '@/pages/Equipment/components/Common/EquipmentDropdown'
import ParameterDropdown from '@/pages/Parameters/components/ParameterDropdown'
import CreateEntityButton from '@/shared/Buttons/CreateEntityButton'
import TrashButton from '@/shared/Buttons/TrashButton'
import { Fragment, useState } from 'react'
import { ITextWidgetParameter, ITextWidgetSettings } from '../../textChart.types'
import css from './textChartSettingContent.module.css'
import RichTextEditor from '../../RichTextEditor'
import { CustomElement } from '../../RichTextEditor/richTextEditor.types'
import { createEditor, Descendant, Transforms } from 'slate'
import { withHistory } from 'slate-history'
import { withReact } from 'slate-react'
import { produce } from 'immer'

interface IProps {
  settings: ITextWidgetSettings
  onChange: (value: any, key: string) => void
  handleParameterTextDelete: (index: number) => void
  handleParameterTextUpdate: (index: number, value: any, key: string) => void
  handleParameterTextCreate: () => void
}

const TextChartSettingContent = (props: IProps) => {
  const [editor] = useState(withHistory(withReact(createEditor())))

  const handleParameterTextCreate = () => {
    const currentSelection = editor.selection

    const nextElement = { text: `$${props.settings.parametersText.length + 1}`, type: 'parameter', fontSize: 14 }

    const newTextValue = produce(props.settings.text, (draft: Descendant[]) => {
      let nodeChildren: Descendant[] = draft

      for (let i = 0; i < currentSelection.anchor.path.length - 1; ++i) {
        // @ts-ignore
        nodeChildren = nodeChildren[currentSelection.anchor.path[i]].children
      }

      // @ts-ignore
      nodeChildren.push(nextElement)
      return draft
    })

    props.onChange(newTextValue, 'text')
    props.handleParameterTextCreate()
  }

  const handleParameterTextDelete = (index: number) => {
    const updatedText = props.settings.text.map((textElement: CustomElement) => {
      if (textElement.children) {
        // Фильтруем удаляемый параметр
        let updatedChildren = textElement.children.filter(
          (child) => !(child.type === 'parameter' && child.text === `$${index + 1}`)
        )

        // Смещаем параметры, которые идут после удаленного
        updatedChildren = updatedChildren.map((child) => {
          if (child.type === 'parameter' && parseInt(child.text.slice(1)) > index + 1) {
            return { ...child, text: `$${parseInt(child.text.slice(1)) - 1}` }
          }
          return child
        })

        // Возвращаем обновленный элемент с удаленными и смещенными параметрами
        return { ...textElement, children: updatedChildren }
      }
      return textElement
    })
    // Если после удаления последнего параметра в props.settings.text остались пустые элементы, их нужно удалить
    const cleanedText = updatedText.filter((textElement) => textElement.children.length > 0)

    props.onChange(cleanedText, 'text')
    props.handleParameterTextDelete(index)

    // Перемещаем курсор после удаления
    Transforms.select(editor, { path: [0, 0], offset: 0 })
  }

  const renderParametersText = (parameterText: ITextWidgetParameter, index: number) => {
    return (
      <Fragment key={index}>
        <div className="inline-flex-gap gap8 width100" style={{ alignItems: 'flex-end', flex: 0 }}>
          <div className={`text-nm ${css.wrapperNumber}`}>
            {index === 0 && <div style={{ marginBottom: '4px' }}>№</div>}
            <div className={css.numberParameter}>${index + 1}</div>
          </div>
          <FormField label={`${index === 0 ? 'Устройство' : ''}`} fullWidth={true}>
            <EquipmentDropdown
              name="deviceId"
              onChange={(val, name) => props.handleParameterTextUpdate(index, val, name)}
              value={parameterText.deviceId}
            />
          </FormField>
          <FormField label={`${index === 0 ? 'Переменная' : ''}`} fullWidth={true}>
            <ParameterDropdown
              value={parameterText.parameterId}
              name="parameterId"
              onChange={(val, name) => props.handleParameterTextUpdate(index, val, name)}
            />
          </FormField>
          <TrashButton
            onClick={() => handleParameterTextDelete(index)}
            style={{ height: '32px', width: '32px', flexShrink: 0 }}
          />
        </div>
      </Fragment>
    )
  }

  return (
    <div className="flex-lines gap16">
      <FormField label="Наименование" required={true}>
        <TextInput
          value={props.settings.title}
          name="title"
          onChange={props.onChange}
          placeHolder="Введите наименование"
        />
      </FormField>
      <SeparateLine />
      <RichTextEditor
        name="text"
        placeHolder="Введите текст. Используйте «$1», «$2» и т. д. для добавления динамических параметров"
        value={props.settings.text}
        onChange={props.onChange}
        editor={editor}
      />
      <SeparateLine />
      <h4 className="text-heading-4">Параметры</h4>
      {props.settings.parametersText.map(renderParametersText)}
      <CreateEntityButton
        onClick={handleParameterTextCreate}
        text="Добавить параметр"
        type="default"
        fullWidth={true}
      />
    </div>
  )
}

export default TextChartSettingContent
